import { Link } from "@inertiajs/react"
import ProductLink from "@/components/productQuickView/ProductLink"
import { Button } from "@/components/ui/Button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover"
import { formatMoneyShort } from "@/lib/money"

export interface UnplannedChannel {
  name: string
  slug: string
  wholesale: boolean
}

// A product that sold in the period with no plan, and the channels missing it.
export interface UnplannedProduct {
  id: string
  name: string
  revenue: number
  channels: UnplannedChannel[]
}

interface Props {
  products: UnplannedProduct[]
  currency: string | null
  // Products rows: the trigger sits in the Plan cell and reads "No plan".
  label?: string
  // Where a channel's plan is filled in and the link's text; null when there
  // is nothing to fill (Wholesale for part of a month).
  linkForChannel: (channel: UnplannedChannel) => UnplannedLink | null
}

export interface UnplannedLink {
  href: string
  label: string
}

const plural = (n: number) => (n === 1 ? "1 product" : `${n} products`)

// "₫20M from 2 products with no plan": opens the list of those products and
// where to fill in each one's plan.
export default function UnplannedNote({ products, currency, label, linkForChannel }: Props) {
  if (products.length === 0) return null
  const revenue = products.reduce((sum, p) => sum + p.revenue, 0)
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="link" className="h-auto p-0 text-xs font-normal">
          {label ?? `${formatMoneyShort(revenue, currency)} from ${plural(products.length)} with no plan`}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 text-left text-sm">
        <div className="border-b px-3 py-2">
          <div className="font-medium">Sold with no plan for these days</div>
          <div className="text-xs">Plan revenue and % of plan leave these sales out.</div>
        </div>
        <ul className="max-h-80 divide-y overflow-auto">
          {products.map((p) => (
            <li key={p.id} className="px-3 py-2">
              <div className="flex justify-between gap-3">
                <ProductLink externalId={p.id} className="font-medium">{p.name}</ProductLink>
                <span className="tabular-nums">{formatMoneyShort(p.revenue, currency)}</span>
              </div>
              {p.channels.map((c) => {
                const link = linkForChannel(c)
                return (
                  <div key={c.slug} className="text-xs">
                    {c.name}:{" "}
                    {link ? (
                      <Link href={link.href} className="text-link hover:underline">
                        {link.label}
                      </Link>
                    ) : (
                      "plan is monthly, pick a whole month"
                    )}
                  </div>
                )
              })}
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  )
}
